import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi } from "../api/hooks";
import type { Proposal, ProposalKind, ProposalStatus } from "../api/types";
import { can, isLocal } from "../auth/roles";
import { StatusBadge } from "../components/Badges";
import { nextStep, ProposalDialog, proposalSummary } from "../components/FourEyes";
import { PROPOSAL_KIND, PROPOSAL_STATUS, labelOf, statusOf } from "../components/labels";
import { Notice, QueryState } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime } from "../format/datetime";
import { personLabel, ScreenHeader, useConfigNames, useWardNames } from "./shared";
import { Button } from "@lcc/ui";
import { Tabs } from "../components/Tabs";

type Mine = "to_confirm" | "proposed" | "all";

/**
 * The four-eyes queue: proposals waiting for me (never my own), those I proposed (to withdraw), and all in my scope;
 * each in words with the next step. Submitted incident reports I may decide are listed too.
 */
export function ApprovalsScreen() {
  const { principal, go } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const [mine, setMine] = useState<Mine>("to_confirm");
  const [kind, setKind] = useState<ProposalKind | "">("");
  const [status, setStatus] = useState<ProposalStatus | "">("");
  const [open, setOpen] = useState<Proposal | null>(null);
  const q = useApi(["approvals", mine, kind, status], (api) => endpoints.listApprovals(api, { mine, kind: kind || undefined, status: status || undefined }), { keepPrevious: true });
  const items = q.data?.items ?? [];
  const reports = q.data?.reports ?? [];
  return (
    <div className="fer-screen">
      <ScreenHeader title="Approvals" />
      {isLocal(principal) && <Notice tone="warning">With the FER local sign-in only incident cancellations are listed.</Notice>}
      <Tabs
        label="Show"
        tabs={[
          { key: "to_confirm" as const, label: "Waiting for me", count: mine === "to_confirm" && q.data ? items.length : undefined },
          { key: "proposed" as const, label: "Proposed by me", count: mine === "proposed" && q.data ? items.length : undefined },
          { key: "all" as const, label: "All I can see", count: mine === "all" && q.data ? items.length : undefined },
        ]}
        current={mine}
        onChange={setMine}
      >
        <div className="fer-toolbar">
          <label className="fer-field fer-field--inline">
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as ProposalKind | "")}>
              <option value="">All</option>
              {Object.entries(PROPOSAL_KIND).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="fer-field fer-field--inline">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as ProposalStatus | "")}>
              <option value="">All</option>
              {Object.entries(PROPOSAL_STATUS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <QueryState isLoading={q.isLoading} error={q.error} label="proposals" />
        {q.data && items.length === 0 && <p className="fer-muted">No proposal.</p>}
        {items.length > 0 && (
          <table className="lcc-table" aria-label={`Proposals (${items.length})`}>
            <thead>
              <tr>
                <th scope="col">Kind</th>
                <th scope="col">What</th>
                <th scope="col">Ward</th>
                <th scope="col">Proposed</th>
                <th scope="col">Expires</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="lcc-sr-only">Review</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const step = nextStep(p, principal);
                return (
                  <tr key={p.proposal_id}>
                    <th scope="row">{labelOf(PROPOSAL_KIND, p.kind)}</th>
                    <td>{proposalSummary(p, names)}</td>
                    <td>{wardName(p.ward_id)}</td>
                    <td>
                      {personLabel(p.proposed_by, principal.partyId)}, {formatDateTime(p.proposed_at)}
                    </td>
                    <td>{formatDateTime(p.expires_at)}</td>
                    <td>
                      <StatusBadge status={statusOf(PROPOSAL_STATUS, p.status)} />
                    </td>
                    <td>
                      <Button variant="secondary" size="sm" onClick={() => setOpen(p)} aria-label={`${step.kind === "confirm" ? "Review and decide" : step.kind === "mine" ? "View or withdraw" : "View"}: ${proposalSummary(p, names)}`}>
                        {step.kind === "confirm" ? "Review and decide" : step.kind === "mine" ? "View or withdraw" : "View"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {reports.length > 0 && (
          <>
            <h3>Incident reports to decide ({reports.length})</h3>
            <ul className="fer-plain-list" aria-label="Incident reports to decide">
              {reports.map((r) => (
                <li key={r.incident_id}>
                  {r.reference} – {names.incidentType(r.incident_type)}, {wardName(r.ward_id)}, submitted {formatDateTime(r.submitted_at)}{" "}
                  {can(principal, "viewIncidents") && (
                    <button type="button" className="fer-link" onClick={() => go("incidents", { incidentId: r.incident_id })}>
                      Open {r.reference}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Tabs>
      <p className="fer-hint">Four eyes: nobody approves their own proposal. A proposal waits for a second person with the right role, then expires.</p>
      {open && <ProposalDialog proposal={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
